/**
 * GroupsView — Group list with create/join CTAs (Podium-style layout, Zporti theme).
 */

import React, { useMemo, useCallback } from 'react';
import { View, Text, ScrollView, Pressable, Platform, useWindowDimensions } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter, useFocusEffect } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, PlusCircle, UserPlus } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useWorldCupGroupsData } from '../../../src/worldcup/hooks/useWorldCupGroupsData';
import { worldCupQueryKeys } from '../../../src/worldcup/constants/worldcupQueryKeys';
import { STALE_TIME_WORLD_CUP_LEADERBOARD_MS } from '../../../src/worldcup/constants/worldcupReactQuery.constants';
import { useCompetitionId, useCompetitionBasePath } from '../../../src/competitions/contexts/CompetitionContext';
import {
  competitionGroupRoute,
  competitionGroupCreateRoute,
  competitionGroupJoinRoute,
  competitionTabRoute,
} from '../../../src/competitions/helpers/competitionRoutes.helper';
import { useSkeletonLoading } from '../../../src/shared/hooks/useSkeletonLoading';
import { FadeInContent } from '../../shared/FadeInContent';
import { StaggeredFadeIn } from '../../shared/StaggeredFadeIn';
import { WorldCupSkeleton } from '../WorldCupSkeleton/WorldCupSkeleton';
import { GroupsViewCard } from './GroupsViewCard';
import { getGroupsViewStyles } from './GroupsView.styles';
import { GROUPS_VIEW_COPY, GROUPS_VIEW_DISCOVERY_GRADIENT_ALPHA } from './GroupsView.constants';

const hoverWeb = Platform.select({ web: WEB_HOVER_TRANSITION_STYLE });

export const GroupsView: React.FC = () => {
  const theme = useTheme();
  const router = useRouter();
  const competitionId = useCompetitionId();
  const basePath = useCompetitionBasePath();
  const queryClient = useQueryClient();
  const { width: windowWidth } = useWindowDimensions();
  const styles = useMemo(() => getGroupsViewStyles(theme, windowWidth), [theme, windowWidth]);
  const { groups, hasGroups, isLoading } = useWorldCupGroupsData();

  /** Company-branded squads pinned to the top; native sort is stable, so tied items keep API order. */
  const orderedGroups = useMemo(
    () => [...groups].sort((a, b) => (a.companyBranding ? 0 : 1) - (b.companyBranding ? 0 : 1)),
    [groups],
  );

  /**
   * Refresh the squad list when the screen regains focus — but only when the
   * cached data has actually gone stale (older than its own staleTime).
   *
   * Previously this force-refetched on EVERY focus (`refetchQueries` ignores
   * staleTime). During a live match the screen re-focuses rapidly, so the squad
   * list reloaded every few seconds for the whole match, rebuilding all squad
   * cards (logos/avatars) each time and growing native memory until an OOM crash
   * (prod Sentry: Android 15/16, builds 44/48). Squads barely change between
   * fetches, and create/join/leave/kick already invalidate this key on change,
   * so gating on staleness keeps passive freshness without the refetch loop.
   *
   * Stable deps: do not depend on hook `refetch` — it was tied to `groupsQuery`
   * identity and changed every fetch, retriggering this effect in a loop.
   */
  useFocusEffect(
    useCallback(() => {
      const state = queryClient.getQueryState(
        worldCupQueryKeys.groups(competitionId),
      );
      const isStale =
        !state?.dataUpdatedAt ||
        Date.now() - state.dataUpdatedAt > STALE_TIME_WORLD_CUP_LEADERBOARD_MS;
      if (!isStale) return;
      void queryClient.refetchQueries({
        queryKey: worldCupQueryKeys.groups(competitionId),
        type: 'active',
      });
    }, [queryClient]),
  );

  const { isLoadingWithDelay, contentOpacity } = useSkeletonLoading({
    isLoading,
    isContentReady: !isLoading,
  });

  const handleGroupPress = useCallback(
    (groupId: string) => {
      router.push(competitionGroupRoute(basePath, groupId) as never);
    },
    [router],
  );

  const handleCreate = useCallback(() => {
    router.push(competitionGroupCreateRoute(basePath) as never);
  }, [router]);

  const handleJoin = useCallback(() => {
    router.push(competitionGroupJoinRoute(basePath) as never);
  }, [router]);

  const handleDiscoverPress = useCallback(() => {
    router.push(competitionTabRoute(basePath, 'leaderboard') as never);
  }, [router]);

  const discoveryGradient = useMemo(
    () => [theme.primary + GROUPS_VIEW_DISCOVERY_GRADIENT_ALPHA, theme.background.cardElevated] as [string, string],
    [theme.primary, theme.background.cardElevated],
  );

  if (isLoadingWithDelay) {
    return <WorldCupSkeleton variant="groups" />;
  }

  return (
    <FadeInContent opacity={contentOpacity} style={styles.container}>
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        <StaggeredFadeIn index={0}>
          <View style={styles.ctaRow}>
            <Pressable
              style={({ pressed, hovered }) => [
                styles.ctaButton,
                styles.ctaButtonCreate,
                hoverWeb,
                pressed && styles.ctaButtonPressed,
                hovered && Platform.OS === 'web' && styles.ctaButtonHover,
              ]}
              onPress={handleCreate}
            >
              <PlusCircle size={22} color={theme.onPrimary} strokeWidth={2.2} />
              <Text style={styles.ctaButtonTextCreate}>{GROUPS_VIEW_COPY.CREATE_TITLE}</Text>
            </Pressable>
            <Pressable
              style={({ pressed, hovered }) => [
                styles.ctaButton,
                styles.ctaButtonJoin,
                hoverWeb,
                pressed && styles.ctaButtonPressed,
                hovered && Platform.OS === 'web' && styles.ctaButtonHover,
              ]}
              onPress={handleJoin}
            >
              <UserPlus size={22} color={theme.textOnCard} strokeWidth={2.2} />
              <Text style={styles.ctaButtonTextJoin}>{GROUPS_VIEW_COPY.JOIN_TITLE}</Text>
            </Pressable>
          </View>
        </StaggeredFadeIn>

        {hasGroups ? (
          orderedGroups.map((group, index) => (
            <StaggeredFadeIn key={group.id} index={index + 1}>
              <GroupsViewCard
                group={group}
                onPress={handleGroupPress}
              />
            </StaggeredFadeIn>
          ))
        ) : (
          <StaggeredFadeIn index={1}>
            <View style={styles.emptyState}>
              <Text style={styles.emptyEmoji}>{GROUPS_VIEW_COPY.EMPTY_ICON}</Text>
              <Text style={styles.emptyTitle}>{GROUPS_VIEW_COPY.EMPTY_TITLE}</Text>
              <Text style={styles.emptySubtitle}>{GROUPS_VIEW_COPY.EMPTY_SUBTITLE}</Text>
            </View>
          </StaggeredFadeIn>
        )}

        <StaggeredFadeIn index={hasGroups ? orderedGroups.length + 1 : 2}>
          <View style={styles.discoverySection}>
            <LinearGradient
              colors={discoveryGradient}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.discoveryGradientFill}
              pointerEvents="none"
            />
            <View style={styles.discoveryInner}>
              <Text style={styles.discoveryTitle}>{GROUPS_VIEW_COPY.DISCOVER_TITLE}</Text>
              <Text style={styles.discoverySubtitle}>{GROUPS_VIEW_COPY.DISCOVER_SUBTITLE}</Text>
              <Pressable
                style={({ pressed, hovered }) => [
                  styles.discoveryCta,
                  hoverWeb,
                  pressed && styles.discoveryCtaPressed,
                  hovered && Platform.OS === 'web' && styles.discoveryCtaHover,
                ]}
                onPress={handleDiscoverPress}
              >
                <Text style={styles.discoveryCtaText}>{GROUPS_VIEW_COPY.DISCOVER_CTA}</Text>
                <ArrowRight size={16} color={theme.primary} strokeWidth={2.5} />
              </Pressable>
            </View>
          </View>
        </StaggeredFadeIn>
      </ScrollView>
    </FadeInContent>
  );
};
